// src/hooks/useTelegramWebApp.ts
import { useEffect, useState } from 'react';

export const useTelegramWebApp = () => {
  const [webApp, setWebApp] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [theme, setTheme] = useState<any>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const [initDataUnsafe, setInitDataUnsafe] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);

  useEffect(() => {
    const checkTelegram = () => {
      if (typeof window === 'undefined') return;
      
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        
        try {
          tg.ready();
        } catch (e) {
          console.error('Error calling ready():', e);
        }
        
        // Устанавливаем тему
        if (tg.themeParams) {
          document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color);
          document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color);
          document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color);
          document.documentElement.style.setProperty('--tg-theme-link-color', tg.themeParams.link_color);
          document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color);
          document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color);
        }
        
        // Извлекаем данные пользователя
        const userData = tg.initDataUnsafe?.user ? {
          id: tg.initDataUnsafe.user.id,
          first_name: tg.initDataUnsafe.user.first_name,
          last_name: tg.initDataUnsafe.user.last_name || '',
          username: tg.initDataUnsafe.user.username || '',
          language_code: tg.initDataUnsafe.user.language_code || 'en',
          is_premium: tg.initDataUnsafe.user.is_premium || false,
          allows_write_to_pm: tg.initDataUnsafe.user.allows_write_to_pm || false,
        } : null;
        
        // Сохраняем данные
        setWebApp(tg);
        setUser(userData);
        setTheme(tg.themeParams);
        setInitData(tg.initData);
        setInitDataUnsafe(tg.initDataUnsafe);
        setPlatform(tg.platform);
        setIsExpanded(tg.isExpanded);
        setIsReady(true);
      }
    };

    checkTelegram();
    
    const handleHashChange = () => {
      checkTelegram();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Автоматическое расширение при загрузке
  useEffect(() => {
    if (isReady && webApp && !isExpanded) {
      try {
        webApp.expand();
      } catch (error) {
        console.error('Failed to expand to full screen:', error);
      }
    }
  }, [isReady, webApp, isExpanded]);

  return {
    webApp,
    user,
    theme,
    initData,
    initDataUnsafe,
    isReady,
    isExpanded,
    platform,
    ready: () => webApp?.ready(),
    expand: () => {
      if (webApp) {
        try {
          return webApp.expand();
        } catch (e) {
          console.error('Expand error:', e);
          return false;
        }
      }
      return false;
    },
    close: () => webApp?.close(),
    enableClosingConfirmation: () => webApp?.enableClosingConfirmation(),
    disableClosingConfirmation: () => webApp?.disableClosingConfirmation(),
    enableVerticalSwipes: () => webApp?.enableVerticalSwipes(),
    disableVerticalSwipes: () => webApp?.disableVerticalSwipes(),
    onEvent: (eventType: string, callback: () => void) => webApp?.onEvent(eventType, callback),
    offEvent: (eventType: string, callback: () => void) => webApp?.offEvent(eventType, callback),
    sendData: (data: string) => webApp?.sendData(data),
    showAlert: (message: string, callback?: () => void) => webApp?.showAlert(message, callback),
    showConfirm: (message: string, callback?: (confirmed: boolean) => void) => webApp?.showConfirm(message, callback),
    showPopup: (params: any, callback?: (buttonId?: string) => void) => webApp?.showPopup(params, callback),
    setHeaderColor: (color: string) => webApp?.setHeaderColor(color),
    setBackgroundColor: (color: string) => webApp?.setBackgroundColor(color),
    setBottomToolbarColor: (color: string) => webApp?.setBottomToolbarColor(color),
    mainButton: {
      setText: (text: string) => webApp?.MainButton.setText(text),
      setColor: (color: string) => webApp?.MainButton.setColor(color),
      setTextColor: (color: string) => webApp?.MainButton.setTextColor(color),
      show: () => webApp?.MainButton.show(),
      hide: () => webApp?.MainButton.hide(),
      enable: () => webApp?.MainButton.enable(),
      disable: () => webApp?.MainButton.disable(),
      onClick: (callback: () => void) => webApp?.MainButton.onClick(callback),
      offClick: (callback: () => void) => webApp?.MainButton.offClick(callback),
      setParams: (params: any) => webApp?.MainButton.setParams(params)
    },
    backButton: {
      show: () => webApp?.BackButton.show(),
      hide: () => webApp?.BackButton.hide(),
      onClick: (callback: () => void) => webApp?.BackButton.onClick(callback),
      offClick: (callback: () => void) => webApp?.BackButton.offClick(callback)
    }
  };
};